import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Car, School } from "lucide-react";
import { useAppDispatch } from "../app/hooks";
import { OrganizationKind } from "../features/organization/enums/OrganizationKind";
import { ApplyPresetResponse } from "../features/organization/contracts/ApplyPresetResponse";
import { chooseOrganizationKind } from "../features/organization/slice-extensions/organization-preset-slice-extension";
import FahrschulePresetForm from "../components/organization/FahrschulePresetForm";
import { ROUTES } from "../routes";
import { calendarWeekPath } from "../components/organization/fahrschulePresetDefaults";

const choiceClass =
  "with-border flex w-full items-start gap-3 rounded-2xl p-4 text-left hover:bg-grey-95 dark:hover:bg-primary-20";

/**
 * First-run step for a new Owner: "what kind of school do you run?". Default goes home
 * as today; Fahrschule opens the preset form and lands on the calendar once applied.
 */
const WelcomeKindPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [fahrschule, setFahrschule] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chooseDefault = async () => {
    setError(null);
    try {
      await dispatch(chooseOrganizationKind(OrganizationKind.Default)).unwrap();
      navigate(ROUTES.HOME, { replace: true });
    } catch (rejected) {
      setError((rejected as { message?: string })?.message ?? null);
    }
  };

  // Land on the week of the first theory evening, so the new series is in view.
  const handleApplied = (result: ApplyPresetResponse, firstTheoryDate: string) => {
    if (result.issues.length === 0) navigate(calendarWeekPath(ROUTES.CALENDAR, firstTheoryDate), { replace: true });
  };

  return (
    <div className="mx-auto w-full max-w-[640px] p-4 py-8">
      <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
        <h1>{t("onboarding.kind.title")}</h1>
        {!fahrschule ? (
          <div className="mt-4 space-y-3 lg:mt-6">
            <button type="button" className={choiceClass} onClick={chooseDefault}>
              <School size={20} className="mt-0.5 shrink-0 text-primary-40 dark:text-primary-90" />
              <span>
                <span className="block font-medium text-grey-10 dark:text-white">{t("onboarding.kind.default")}</span>
                <span className="block text-sm text-grey-30 dark:text-grey-70">{t("onboarding.kind.defaultHint")}</span>
              </span>
            </button>
            <button type="button" className={choiceClass} onClick={() => setFahrschule(true)}>
              <Car size={20} className="mt-0.5 shrink-0 text-primary-40 dark:text-primary-90" />
              <span>
                <span className="block font-medium text-grey-10 dark:text-white">{t("onboarding.kind.fahrschule")}</span>
                <span className="block text-sm text-grey-30 dark:text-grey-70">{t("onboarding.kind.fahrschuleHint")}</span>
              </span>
            </button>
          </div>
        ) : (
          <div className="mt-4 lg:mt-6">
            <button
              type="button"
              className="mb-4 inline-flex items-center gap-1 text-sm text-primary-40 hover:underline dark:text-primary-90"
              onClick={() => setFahrschule(false)}
            >
              <ArrowLeft size={16} />
              <span>{t("onboarding.kind.back")}</span>
            </button>
            <FahrschulePresetForm onApplied={handleApplied} />
          </div>
        )}
        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </div>
  );
};

export default WelcomeKindPage;
